import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Banner, Button, CkThemeProvider, Field, Panel, Spinner, Tag, TextArea, TextInput, useCkInside,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api';
import { Toggle } from '../../next/ui';

const MAIL_STATUS = {
  sent: { label: 'נשלח', tone: 'success' },
  failed: { label: 'השליחה נכשלה', tone: 'danger' },
  pending: { label: 'לא נשלח', tone: 'warning' },
};
const KIND_LABEL = { feedback: 'משוב', request: 'בקשה' };

const errorText = (e) => e?.response?.data?.error || 'הפעולה לא הושלמה. יש לנסות שוב';

/** מחליף מילים שמורות בערכים לדוגמה — תצוגה מקדימה בלבד; בשליחה הערכים מגיעים מהמסד */
function fillSamples(template, tags) {
  const samples = Object.fromEntries(tags.map((t) => [t.tag, t.sample]));
  return template.replace(/\{([^{}]*)\}/g, (match, name) => (Object.hasOwn(samples, name) ? samples[name] : match));
}

function formatDate(value) {
  return value ? new Date(value).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' }) : '';
}

function EmailAutomationsBody() {
  const [state, setState] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  // השדה שבו הסמן — לשם נכנסת המילה השמורה
  const activeField = useRef({ name: 'body', el: null });
  const [recipient, setRecipient] = useState('');
  const [testTo, setTestTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const apply = useCallback((data) => {
    setState(data);
    setSubject(data.examResult.subject);
    setBody(data.examResult.body);
    setRecipient(data.feedback.recipient);
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.get('/email-automations')
      .then(({ data }) => { if (!cancelled) apply(data); })
      .catch((e) => { if (!cancelled) setError(errorText(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [apply]);

  const run = async (operation, doneMessage) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await operation();
      setNotice(doneMessage);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const save = (body, doneMessage) => run(async () => {
    const { data } = await api.put('/email-automations', body);
    apply(data);
  }, doneMessage);

  const trackField = (name) => (e) => { activeField.current = { name, el: e.target }; };

  const insertTag = (tag) => {
    const { name, el } = activeField.current;
    const current = name === 'subject' ? subject : body;
    const start = el ? el.selectionStart : current.length;
    const end = el ? el.selectionEnd : current.length;
    const token = `{${tag}}`;
    (name === 'subject' ? setSubject : setBody)(current.slice(0, start) + token + current.slice(end));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  if (loading) return <span role="status"><Spinner size={18} /> טוען…</span>;
  if (!state) return error ? <Banner tone="danger">{error}</Banner> : null;

  const { examResult, feedback, senderActive, membersWithEmail } = state;

  return (
    <div className="nx-stack" dir="rtl">
      {error ? <Banner tone="danger" onClose={() => setError('')}>{error}</Banner> : null}
      {notice ? <Banner tone="success" onClose={() => setNotice('')}>{notice}</Banner> : null}
      {!senderActive ? (
        <Banner tone="warning" icon="alert">
          אין דומיין שליחה פעיל, ולכן מיילים לא יישלחו. הודעות משוב עדיין נשמרות ומוצגות כאן.
        </Banner>
      ) : null}

      <Panel title="מייל עידוד וציון בסיום מבחן">
        <div className="nx-stack">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Toggle
              checked={examResult.enabled}
              disabled={busy}
              label="שליחה אוטומטית בסיום מבחן"
              onChange={(on) => save({ examResult: { enabled: on } }, on ? 'המייל האוטומטי הופעל' : 'המייל האוטומטי כובה')}
            />
            <span>שליחה אוטומטית בסיום מבחן</span>
          </div>
          <Field label="מילים שמורות" hint="לחיצה מכניסה את המילה במקום הסמן, בכותרת או בגוף המייל. בשליחה היא מוחלפת בערך של הנבחן מהמסד.">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {examResult.tags.map(({ tag }) => (
                <Button key={tag} size="sm" onMouseDown={(e) => e.preventDefault()} onClick={() => insertTag(tag)}>{tag}</Button>
              ))}
            </div>
          </Field>
          <Field label="כותרת המייל">
            <TextInput value={subject} maxLength={200} onFocus={trackField('subject')} onChange={(e) => setSubject(e.target.value)} />
          </Field>
          <Field label="גוף המייל" hint={`נשלח בדיוק כפי שנכתב כאן. בסוף כל מייל מתווספת רק השורה: ${examResult.footer}`}>
            <TextArea rows={9} value={body} maxLength={5000} onFocus={trackField('body')} onChange={(e) => setBody(e.target.value)} />
          </Field>
          <div>
            <Button
              disabled={busy || !subject.trim() || !body.trim() || (subject === examResult.subject && body === examResult.body)}
              onClick={() => save({ examResult: { subject, body } }, 'המייל נשמר')}
            >
              שמירת המייל
            </Button>
          </div>
          <Field label="תצוגה מקדימה, עם ערכים לדוגמה">
            <div style={{ border: '1px solid var(--ck-border)', borderRadius: 8, padding: 12 }}>
              <b>{fillSamples(subject, examResult.tags)}</b>
              <div style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>{fillSamples(body, examResult.tags)}</div>
              <div className="ck-muted" style={{ fontSize: 13, marginTop: 8 }}>{examResult.footer}</div>
            </div>
          </Field>
          <Field label="מייל ניסיון" hint="נשלח עם הכותרת והגוף השמורים וערכים לדוגמה, לכתובת הזו בלבד.">
            <TextInput ltr type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
          </Field>
          <div>
            <Button
              disabled={busy || !testTo.trim() || !senderActive}
              onClick={() => run(() => api.post('/email-automations/exam-result/test', { to: testTo }), 'מייל הניסיון נשלח')}
            >
              שליחת ניסיון
            </Button>
          </div>
          <dl className="nx-kv">
            <div className="nx-kv__row"><dt>נבחנים פעילים עם כתובת מייל</dt><dd>{membersWithEmail}</dd></div>
            <div className="nx-kv__row"><dt>נשלחו</dt><dd>{examResult.successCount}</dd></div>
            <div className="nx-kv__row"><dt>נכשלו</dt><dd>{examResult.failureCount}</dd></div>
            {examResult.lastSentAt ? <div className="nx-kv__row"><dt>שליחה אחרונה</dt><dd>{formatDate(examResult.lastSentAt)}</dd></div> : null}
          </dl>
        </div>
      </Panel>

      <Panel title="משוב ובקשות מהפורטל">
        <div className="nx-stack">
          <Field label="כתובת לקבלת ההודעות" hint="כל הודעה מהאזור האישי תישלח לכתובת הזו.">
            <TextInput ltr type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} />
          </Field>
          <div>
            <Button
              disabled={busy || recipient.trim().toLowerCase() === feedback.recipient}
              onClick={() => save({ feedback: { recipient } }, 'הכתובת נשמרה')}
            >
              שמירת הכתובת
            </Button>
          </div>
          {feedback.recent.length ? (
            <div className="nx-stack">
              <b>הודעות אחרונות</b>
              {feedback.recent.map((f) => (
                <div key={f.id} style={{ borderTop: '1px solid var(--ck-border)', paddingTop: 8 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    <b>{f.memberName || 'ללא שם'}</b>
                    <Tag>{KIND_LABEL[f.kind] || f.kind}</Tag>
                    <Tag tone={MAIL_STATUS[f.mailStatus]?.tone}>{MAIL_STATUS[f.mailStatus]?.label || f.mailStatus}</Tag>
                    <span className="ck-muted" style={{ fontSize: 13 }}>{formatDate(f.createdAt)}</span>
                  </div>
                  <div className="ck-muted" style={{ fontSize: 13 }} dir="ltr">
                    {[f.phone, f.email].filter(Boolean).join(' · ')}
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{f.message}</div>
                </div>
              ))}
            </div>
          ) : <span className="ck-muted">עדיין לא התקבלו הודעות.</span>}
        </div>
      </Panel>
    </div>
  );
}

/** crm-kit/next component; outside a next screen (old UI) it brings its own theme root */
export default function EmailAutomationsSettings() {
  const inside = useCkInside();
  return inside ? <EmailAutomationsBody /> : <CkThemeProvider><EmailAutomationsBody /></CkThemeProvider>;
}
